import { useMemo } from "react"
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useMeetingsStore } from "../../store/meetings.store"

const DAY_KEYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

export default function MeetingsDurationCard() {
  const t = useTranslation()
  const meetings = useMeetingsStore((s) => s.meetings)

  const totalMins = useMemo(() => meetings.reduce((acc, m) => acc + (m.duration ?? 0), 0), [meetings])
  const totalHrs  = Math.round(totalMins / 60 * 10) / 10

  const durationData = useMemo(() => {
    const byDay: Record<string, number> = { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 }
    meetings.forEach(m => {
      const day = DAY_KEYS[new Date(m.startTimeMs).getDay()]
      byDay[day] = (byDay[day] ?? 0) + Math.round((m.duration ?? 0) / 60 * 10) / 10
    })
    return DAY_KEYS.map(day => ({ day, value: byDay[day] }))
  }, [meetings])

  return (
    <div className="flex-1 bg-card rounded-[18px] shadow-sm overflow-hidden">
      {/* Header with border bottom */}
      <div className="px-4 py-3 border-b border-stroke-100 dark:border-border">
        <h3 className="text-sm font-semibold text-foreground">{t("meetings.meetingsDuration")}</h3>
      </div>

      {/* Content with graph on top and details below in column */}
      <div className="p-4 flex flex-col">
         <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-muted-foreground">
              {t("meetings.totalMeetingsDuration")}
            </p>
               <div className="flex flex-col">
          <div className="flex items-center gap-1">
          <div className="flex items-baseline">
            <span className="text-sm font-extrabold text-foreground">
              {totalHrs}
            </span>
            <span className="text-sm font-semibold text-muted-foreground">
              {t("common.hoursShort")}
            </span>
          </div>

          <p className="text-xs text-muted-foreground mt-1">
            {totalMins} {t("common.minutesShort") ?? "mins"}
          </p>
</div>
         
        </div>
          </div>
        <div className="mb-3 h-[120px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={durationData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <XAxis
                dataKey="day"
                fontSize={10}
                stroke="currentColor"
                tickLine={false}
                axisLine={false}
                interval={0}
                tickMargin={8}
                padding={{ left: 4, right: 4 }}
              />
              <YAxis hide domain={[0, "dataMax + 1"]} />
              {/* recharts' own tooltip is always white, and its day label takes the
                  page's text colour, so in dark mode it was white on white. */}
              <Tooltip
                cursor={{ stroke: "var(--border)", strokeDasharray: "3 3" }}
                contentStyle={{ background: "var(--card)", borderColor: "var(--border)" }}
                labelStyle={{ color: "var(--foreground)" }}
                itemStyle={{ color: "inherit" }}
                wrapperClassName="text-primary"
                formatter={(value: number) => [`${value} ${t("common.hoursShort")}`, t("meetings.duration")]}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="var(--primary)"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "var(--primary)" }}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

     
      </div>
    </div>
  )
}
